import {
  Button, Space, Tooltip, Tag, message, Breadcrumb, Input, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  HomeOutlined, SearchOutlined,
} from '@ant-design/icons';
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { staffApi } from '../../../api/staff.api';
import { designationApi } from '../../../api/designation.api';
import DataTable from '../../../components/common/DataTable';
import StaffForm from '../components/StaffForm';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';

const StaffList = () => {
  const [formOpen,     setFormOpen]     = useState(false);
  const [editRecord,   setEditRecord]   = useState(null);
  const [searchInput,  setSearchInput]  = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination,   setPagination]   = useState({ page: 1, limit: 10 });
  const [sort,         setSort]         = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  const debouncedSearch = useDebounce(searchInput, 300);

  // ── Designations lookup (DB) ─────────────────────────────────────
  const { data: designationsData } = useQuery({
    queryKey: ['designations'],
    queryFn:  designationApi.getAll,
    staleTime: 5 * 60 * 1000,
  });

  // case-insensitive map: stored value → DB display name
  const designationMap = useMemo(() => {
    const map = {};
    (designationsData?.data || []).forEach((d) => {
      map[d.name.toLowerCase()] = d.name;
    });
    return map;
  }, [designationsData]);

  const resolveDesignation = (v) => (v ? (designationMap[v.toLowerCase()] ?? v) : '—');

  // ── Data ────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    // Use primitive values so React Query compares correctly without deep-object issues
    queryKey: ['staff', debouncedSearch, statusFilter, sort.sortBy, sort.sortOrder, pagination.page, pagination.limit],
    queryFn:  () => staffApi.getAll({ search: debouncedSearch, ...(statusFilter ? { status: statusFilter } : {}), ...sort, ...pagination }),
    staleTime: 0, // always fetch fresh data when sort / page / search changes
  });

  // ── Mutations ────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: staffApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      message.success('Staff added — setup email sent to their inbox');
      setFormOpen(false);
    },
    onError: (err) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => staffApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['staff']);
      message.success('Staff updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: staffApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['staff']); message.success('Staff deleted'); },
    onError: (err) => message.error(err.message),
  });

  const handleFormSubmit = async (values) => {
    try {
      if (editRecord) { await updateMutation.mutateAsync({ id: editRecord.id, data: values }); }
      else            { await createMutation.mutateAsync(values); }
    } catch { /* error shown by onError */ }
  };

  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const handleTableChange = useCallback((_pag, _filters, sorter, extra) => {
    // extra.action distinguishes sort clicks from pagination / filter changes
    if (extra?.action !== 'sort') return;
    const s = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = s?.field || s?.columnKey;
    setSort(s?.order && field
      ? { sortBy: field, sortOrder: s.order === 'ascend' ? 'asc' : 'desc' }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const allStaff = data?.data || [];
  const meta     = data?.meta || {};

  const getSortOrder = (field) => (
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null
  );

  // ── Columns ──────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Staff Name', dataIndex: 'name', key: 'name',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('name'),
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Mobile No.', dataIndex: 'phone', key: 'phone',
      width: 140, sorter: true, sortOrder: getSortOrder('phone'),
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email',
      width: 200, ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('email'),
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—',
    },
    {
      title: 'Designation', dataIndex: 'designation', key: 'designation',
      width: 140, ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('designation'),
      render: (v) => {
        const label = resolveDesignation(v);
        return label !== '—' ? <Tooltip title={label}>{label}</Tooltip> : '—';
      },
    },
    {
      title: 'School', key: 'school', width: 180,
      ellipsis: { showTitle: false },
      render: (_, r) => {
        const name = r.school?.name || '—';
        return name !== '—' ? <Tooltip title={name}>{name}</Tooltip> : '—';
      },
    },
    {
      title: 'Status', dataIndex: 'isActive', key: 'isActive',
      width: 100, sorter: true, sortOrder: getSortOrder('isActive'),
      render: (v) => (
        <Tag
          color={v !== false ? 'success' : 'error'}
          style={{ borderRadius: 9999, fontSize: 11 }}
        >
          {v !== false ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 90,
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => { setEditRecord(record); setFormOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              shape="circle"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
              onClick={() => showDeleteConfirm({
                content: 'Delete this staff member?',
                onOk: () => deleteMutation.mutate(record.id),
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Staff Management' },
        ]}
      />

      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by name, mobile, email, school…"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, maxWidth: 340, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        /> 
        <Select
          value={statusFilter}
          onChange={(val) => { setStatusFilter(val); setPagination((p) => ({ ...p, page: 1 })); }}
          style={{ width: 130 }}
          options={[
            { label: 'All',      value: '' },
            { label: 'Active',   value: 'active' },
            { label: 'Inactive', value: 'inactive' },
          ]}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => { setEditRecord(null); setFormOpen(true); }}
        >
          Add Staff
        </Button>
      </div>

      <DataTable
        columns={columns}
        dataSource={allStaff}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 900 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
          onChange: (page, limit) => setPagination({ page, limit }),
        }}
        onChange={handleTableChange}
      />

      <StaffForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
      />
    </div>
  );
};

export default StaffList;
